import React from 'react'
import { Button } from './button'

interface AudioPlayButtonProps {
  text: string
  level?: string
  voiceType?: 'male' | 'female'
  role?: string
  isPlaying: boolean
  isGenerating: boolean
  onPlay: () => void
  className?: string
  size?: 'default' | 'sm' | 'lg' | 'icon' | null
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' | null
}

export const AudioPlayButton: React.FC<AudioPlayButtonProps> = ({
  text,
  level = 'default',
  voiceType = 'female',
  role,
  isPlaying,
  isGenerating,
  onPlay,
  className = '',
  size = 'sm',
  variant = 'secondary'
}) => {
  // 處理點擊事件（支援滑鼠和觸控）
  const handleClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation()
    e.preventDefault()
    // 防止在播放中或生成中時觸發
    if (isPlaying || isGenerating) {
      return
    }
    onPlay()
  }

  // 專門處理觸控事件（手機端）- 加強事件阻止
  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation()
    e.nativeEvent.stopImmediatePropagation()
    // 防止在播放中或生成中時觸發
    if (isPlaying || isGenerating) {
      return
    }
    onPlay()
  }

  // 額外的觸摸事件處理，確保完全阻止冒泡
  const handleTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation()
    e.nativeEvent.stopImmediatePropagation()
  }

  const getButtonContent = () => {
    if (isGenerating) {
      return (
        <div className="flex items-center space-x-1">
          <div className="flex space-x-1">
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
          </div>
          <span className="text-xs">生成中</span>
        </div>
      )
    }

    if (isPlaying) {
      return (
        <div className="flex items-center space-x-1">
          <div className="flex space-x-1">
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" style={{animationDelay: '200ms'}} />
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" style={{animationDelay: '400ms'}} />
          </div>
          <span className="text-xs">播放中</span>
        </div>
      )
    }

    return '🔊'
  }

  return (
    <Button
      variant={variant}
      size={size}
      disabled={isPlaying || isGenerating}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseDown={(e) => e.stopPropagation()} // 防止滑鼠事件冒泡
      className={`${className} touch-manipulation`}
      title={`播放: ${text}`}
      style={{
        // 確保觸控區域足夠大，且響應迅速
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        pointerEvents: 'auto' // 確保可以接收所有事件
      }}
    >
      {getButtonContent()}
    </Button>
  )
}
